// hooks/useCacheManager.js
import { useState, useEffect, useCallback } from 'react';
import BrandDataManager from '../utils/BrandDataManager';
import backgroundRefreshService from '../services/BackgroundRefreshService';

/**
 * Hook for managing cache state and background refresh
 */
export function useCacheManager() {
  const [cacheStatus, setCacheStatus] = useState(() => BrandDataManager.getCacheStatus());
  const [refreshStatus, setRefreshStatus] = useState({
    isRefreshing: false,
    lastRefresh: null,
    error: null
  });

  // Update cache status
  const updateCacheStatus = useCallback(() => {
    setCacheStatus(BrandDataManager.getCacheStatus());
  }, []);

  // Handle refresh events
  const handleRefreshEvent = useCallback((event, data) => {
    switch (event) {
      case 'refreshStart':
        setRefreshStatus(prev => ({
          ...prev,
          isRefreshing: true,
          error: null
        }));
        break;

      case 'refreshSuccess':
        setRefreshStatus(prev => ({
          ...prev,
          isRefreshing: false,
          lastRefresh: data.refreshTime,
          error: null
        }));
        updateCacheStatus();
        break;

      case 'refreshError':
        setRefreshStatus(prev => ({
          ...prev,
          isRefreshing: false,
          error: data.error.message
        }));
        break;

      case 'refreshEnd':
        setRefreshStatus(prev => ({
          ...prev,
          isRefreshing: false
        }));
        break;
    }
  }, [updateCacheStatus]);

  // Set up listeners
  useEffect(() => {
    const removeListener = backgroundRefreshService.addListener(handleRefreshEvent);
    
    // Update cache status periodically
    const statusInterval = setInterval(updateCacheStatus, 30000); // Every 30 seconds

    return () => {
      removeListener();
      clearInterval(statusInterval);
    };
  }, [handleRefreshEvent, updateCacheStatus]);

  // Start background service on mount
  useEffect(() => {
    backgroundRefreshService.start();
    
    return () => {
      // Don't stop the service when component unmounts
      // It should run globally
    };
  }, []);

  // Manual refresh function
  const forceRefresh = useCallback(async () => {
    await backgroundRefreshService.forceRefresh();
  }, []);

  // Clear cache function
  const clearCache = useCallback(() => {
    BrandDataManager.clearLocalStorage();
    BrandDataManager.cache.data = null;
    BrandDataManager.cache.timestamp = null;
    updateCacheStatus();
  }, [updateCacheStatus]);

  return {
    cacheStatus,
    refreshStatus,
    forceRefresh,
    clearCache,
    updateCacheStatus,
    serviceStatus: backgroundRefreshService.getStatus()
  };
}

/**
 * Hook for cache-aware data loading
 */
export function useCachedData(dataLoader, dependencies = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { cacheStatus } = useCacheManager();

  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await dataLoader(forceRefresh);
      setData(result);
    } catch (err) {
      setError(err.message);
      console.error('Error loading cached data:', err);
    } finally {
      setLoading(false);
    }
  }, [dataLoader]);

  // Initial load
  useEffect(() => {
    loadData();
  }, dependencies);

  // Reload when cache is updated
  useEffect(() => {
    if (cacheStatus.hasMemoryCache && data === null) {
      loadData();
    }
  }, [cacheStatus.hasMemoryCache, data, loadData]);

  return {
    data,
    loading,
    error,
    reload: loadData,
    cacheStatus
  };
}

/**
 * Hook for monitoring cache performance
 */
export function useCachePerformance() {
  const [metrics, setMetrics] = useState({
    hitRate: 0,
    missRate: 0,
    averageLoadTime: 0,
    totalRequests: 0
  });

  const [requestLog, setRequestLog] = useState([]);

  const recordRequest = useCallback((type, duration, fromCache = false) => {
    const request = {
      type,
      duration,
      fromCache,
      timestamp: Date.now()
    };

    setRequestLog(prev => {
      const newLog = [...prev, request].slice(-100); // Keep last 100 requests
      
      // Calculate metrics
      const totalRequests = newLog.length;
      const cacheHits = newLog.filter(r => r.fromCache).length;
      const hitRate = totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0;
      const missRate = 100 - hitRate;
      const averageLoadTime = newLog.reduce((sum, r) => sum + r.duration, 0) / totalRequests;

      setMetrics({
        hitRate: Math.round(hitRate * 100) / 100,
        missRate: Math.round(missRate * 100) / 100,
        averageLoadTime: Math.round(averageLoadTime * 100) / 100,
        totalRequests
      });

      return newLog;
    });
  }, []);

  return {
    metrics,
    requestLog,
    recordRequest
  };
}

export default useCacheManager;